/**
 * UI Configuration Utility
 * Module visibility is controlled by pipeline sed commands that modify getDefaultUIConfig()
 * No caching - all modules use pipeline-generated defaults
 */

/**
 * Default UI configuration - all modules visible by default
 */
const getDefaultUIConfig = () => ({
  wnt: {
    singleLineDiagram: true,
    geographicalMap: true,
    digitalTwin: true,
    solarOutputPrediction: true
  },
  cybershield: {
    posture: true,
    rim: false
  },
  netZero: {
    dashboard: true
  },
  ea: {
    dashboard: true
  },
  em: {
    dashboard: true
  },
  drDashboard: {
    dashboard: false
  },
  solarEnergy: {
    dashboard: false
  },
  prooferTool: {
    dashboard: false
  },
  cognien: {
    alarmModule: true,
    historicalModule: true,
    sldModule: true
  }
});

/**
 * Get the current UI configuration - always returns pipeline defaults
 */
export const getUIConfig = async () => {
  return getDefaultUIConfig();
};

/**
 * Get the current UI configuration synchronously
 * Always returns pipeline defaults (no caching)
 */
export const getUIConfigSync = () => {
  return getDefaultUIConfig();
};

/**
 * Save UI configuration - DEPRECATED
 * Configuration is now controlled only via Run UI Pipeline button
 */
export const saveUIConfig = async (config) => {
  console.log('⚠️ saveUIConfig is deprecated - use Run UI Pipeline button instead');
  return false;
};

/**
 * Build UI configuration from backend module toggles
 * Maps backend module names to UI visibility settings
 * @param {Object} backendConfig - Backend configuration with module toggles
 * @returns {Object} UI configuration object
 */
export const buildUIConfigFromBackendConfig = (backendConfig) => {
  const uiConfig = {
    wnt: {
      singleLineDiagram: backendConfig.wntModules?.wntSld?.enabled ?? true,
      geographicalMap: backendConfig.wntModules?.wntGeoMap?.enabled ?? true,
      digitalTwin: backendConfig.wntModules?.wntDigitalTwin?.enabled ?? true,
      solarOutputPrediction: backendConfig.wntModules?.wntSolarOutputPrediction?.enabled ?? true
    },
    cybershield: {
      posture: backendConfig.cybershieldModules?.cybershieldPosture?.enabled ?? true,
      rim: backendConfig.cybershieldModules?.cybershieldRim?.enabled ?? true
    },
    netZero: {
      dashboard: backendConfig.netZeroModules?.bedasNetZeroModule?.enabled ?? true
    },
    ea: {
      dashboard: backendConfig.cybershieldModules?.eaModule?.enabled ?? true
    },
    em: {
      dashboard: backendConfig.cybershieldModules?.emWidget?.enabled ?? true
    },
    drDashboard: {
      dashboard: backendConfig.drDashboardModules?.drDashboard?.enabled ?? true
    },
    solarEnergy: {
      dashboard: backendConfig.solarEnergyModules?.solarEnergy?.enabled ?? true
    },
    prooferTool: {
      dashboard: backendConfig.prooferToolModules?.prooferTool?.enabled ?? true
    },
    cognien: {
      alarmModule: backendConfig.uiModules?.bedasAlarmModule?.enabled ?? true,
      historicalModule: backendConfig.uiModules?.bedasHistoricalModule?.enabled ?? true,
      sldModule: backendConfig.uiModules?.bedasSldModule?.enabled ?? true
    }
  };

  return uiConfig;
};

/**
 * Check if a specific WNT module should be visible in UI
 * @param {string} moduleKey - Module key (singleLineDiagram, geographicalMap, digitalTwin, solarOutputPrediction)
 * @returns {boolean}
 */
export const isWNTModuleVisible = (moduleKey) => {
  const uiConfig = getUIConfigSync();
  return uiConfig.wnt?.[moduleKey] ?? true;
};

/**
 * Check if Cybershield module should be visible in UI
 * @param {string} moduleKey - Module key (posture, rim)
 * @returns {boolean}
 */
export const isCybershieldModuleVisible = (moduleKey) => {
  const uiConfig = getUIConfigSync();
  return uiConfig.cybershield?.[moduleKey] ?? true;
};

/**
 * Check if NetZero solution should be visible in UI
 * @returns {boolean}
 */
export const isNetZeroVisible = () => {
  const uiConfig = getUIConfigSync();
  return uiConfig.netZero?.dashboard ?? true;
};

/**
 * Check if EA solution should be visible in UI
 * @returns {boolean}
 */
export const isEAVisible = () => {
  const uiConfig = getUIConfigSync();
  return uiConfig.ea?.dashboard ?? true;
};

/**
 * Check if EM solution should be visible in UI
 * @returns {boolean}
 */
export const isEMVisible = () => {
  const uiConfig = getUIConfigSync();
  return uiConfig.em?.dashboard ?? true;
};

/**
 * Check if DR Dashboard solution should be visible in UI
 * @returns {boolean}
 */
export const isDRDashboardVisible = () => {
  const uiConfig = getUIConfigSync();
  return uiConfig.drDashboard?.dashboard ?? true;
};

/**
 * Check if Solar Energy solution should be visible in UI
 * @returns {boolean}
 */
export const isSolarEnergyVisible = () => {
  const uiConfig = getUIConfigSync();
  return uiConfig.solarEnergy?.dashboard ?? true;
};

/**
 * Check if Proofer Tool solution should be visible in UI
 * @returns {boolean}
 */
export const isProoferToolVisible = () => {
  const uiConfig = getUIConfigSync();
  return uiConfig.prooferTool?.dashboard ?? true;
};

/**
 * Check if a specific Cognien module should be visible in UI
 * @param {string} moduleKey - Module key (alarmModule, historicalModule, sldModule)
 * @returns {boolean}
 */
export const isCognienModuleVisible = (moduleKey) => {
  const uiConfig = getUIConfigSync();
  return uiConfig.cognien?.[moduleKey] ?? true;
};

/**
 * Check if any WNT modules are visible
 * @returns {boolean}
 */
export const hasVisibleWNTModules = () => {
  const uiConfig = getUIConfig();
  return Object.values(uiConfig.wnt || {}).some(visible => visible === true);
};

/**
 * Get array of visible WNT modules
 * @returns {Array<string>} Array of visible module keys
 */
export const getVisibleWNTModules = () => {
  const uiConfig = getUIConfig();
  return Object.entries(uiConfig.wnt || {})
    .filter(([_, visible]) => visible === true)
    .map(([key, _]) => key);
};

/**
 * Reset UI configuration - DEPRECATED (no cache exists)
 */
export const resetUIConfig = () => {
  console.log('⚠️ resetUIConfig is deprecated - no cache to reset');
};

/**
 * Check if UI configuration exists - DEPRECATED
 */
export const hasUIConfig = () => {
  return false; // No cache, always use defaults
};

/**
 * Module mapping for display purposes
 */
export const WNT_MODULE_MAPPING = {
  wntSld: {
    uiKey: 'singleLineDiagram',
    displayName: 'Single Line Diagram',
    tabLabel: 'Single Line Diagram'
  },
  wntGeoMap: {
    uiKey: 'geographicalMap',
    displayName: 'Geographical Map',
    tabLabel: 'Geographical Map'
  },
  wntDigitalTwin: {
    uiKey: 'digitalTwin',
    displayName: 'Digital Twin',
    tabLabel: 'Digital Twin'
  },
  wntSolarOutputPrediction: {
    uiKey: 'solarOutputPrediction',
    displayName: 'Solar Output Prediction',
    tabLabel: 'Solar Output Prediction'
  }
};

export const SOLUTION_MAPPING = {
  cybershieldPosture: {
    uiKey: 'posture',
    displayName: 'CyberShield Posture',
    navbarTab: 'Posture Score'
  },
  cybershieldRim: {
    uiKey: 'rim',
    displayName: 'CyberShield RIM',
    navbarTab: 'RIM'
  },
  netZero: {
    uiKey: 'dashboard',
    displayName: 'NetZero',
    navbarTab: 'NetZero',
    route: '/nz'
  },
  ea: {
    uiKey: 'dashboard',
    displayName: 'EA',
    navbarTab: 'EA',
    route: '/ea'
  },
  em: {
    uiKey: 'dashboard',
    displayName: 'EM (EVCI EDS)',
    navbarTab: 'EVCI EDS',
    route: '/evci-eds'
  },
  drDashboard: {
    uiKey: 'dashboard',
    displayName: 'DR Dashboard',
    navbarTab: 'DR Dashboard',
    route: '/dr-dashboard'
  },
  solarEnergy: {
    uiKey: 'dashboard',
    displayName: 'Solar Energy',
    navbarTab: 'Solar Energy',
    route: '/solar-energy'
  },
  prooferTool: {
    uiKey: 'dashboard',
    displayName: 'Proofer Tool',
    navbarTab: 'Proofer Tool',
    route: '/proofer-tool'
  }
};
